export const getCurrentPropertyDetails = ({ validatedAddress, currentPropertySales, sales }) => {
  let currentPaon = '';
  let currentStreet = '';

  if (validatedAddress && validatedAddress.street_number && validatedAddress.street) {
    currentPaon = String(validatedAddress.street_number).trim();
    currentStreet = validatedAddress.street.trim().toLowerCase();
  } else if (currentPropertySales && currentPropertySales.length > 0) {
    currentPaon = String(currentPropertySales[0]?.address?.paon || '').trim();
    currentStreet = (currentPropertySales[0]?.address?.street || '').trim().toLowerCase();
  } else if (sales && sales.length > 0) {
    currentPaon = String(sales[0]?.address?.paon || '').trim();
    currentStreet = (sales[0]?.address?.street || '').trim().toLowerCase();
  }
  return { currentPaon, currentStreet };
};

export const determinePropertyType = ({ validatedAddress, sales, currentPaon, currentStreet }) => {
  let propertyType = 'Unknown';
  if (validatedAddress && validatedAddress.property_type) {
    propertyType = validatedAddress.property_type.toLowerCase();
  } else if (sales && sales.length > 0) {
    const match = sales.find((sale) => {
      const salePaon = String(sale.address?.paon || '').trim();
      const saleStreet = (sale.address?.street || '').trim().toLowerCase();
      return (
        salePaon === currentPaon &&
        (saleStreet.includes(currentStreet) || currentStreet.includes(saleStreet))
      );
    });
    propertyType = (match && match.propertyType) || 'Unknown';
  }
  return propertyType;
};

export const getCurrentFlatNumber = (validatedAddress) => {
  if (validatedAddress?.flat_number) {
    return validatedAddress.flat_number
      .replace(/^flat\s+/i, '')
      .trim()
      .toLowerCase();
  }
  return null;
};

export const formatAllSalesData = (sales, { currentPaon, currentStreet, currentFlatNumber }) => {
  const allSalesData = sales.map((sale) => {
    const salePaon = String(sale.address?.paon || '').trim();
    const saleStreet = String(sale.address?.street || '')
      .trim()
      .toLowerCase();
    const saleFlatNumber = sale.address?.saon
      ? String(sale.address.saon)
          .replace(/^flat\s+/i, '')
          .trim()
          .toLowerCase()
      : null;
    const basicMatch =
      salePaon === currentPaon &&
      (saleStreet.includes(currentStreet) || currentStreet.includes(saleStreet));
    const isCurrentProperty = currentFlatNumber
      ? basicMatch && saleFlatNumber === currentFlatNumber
      : basicMatch;
    return {
      x: new Date(sale.date).getTime(),
      y: sale.price,
      address: `${sale.address.saon ? `${sale.address.saon}, ` : ''}${sale.address.paon || ''} ${
        sale.address.street || ''
      }`.trim(),
      propertyType: sale.propertyType || 'Unknown',
      estateType: sale.estateType || 'Unknown',
      newBuild: sale.newBuild || false,
      date: sale.date,
      flatNumber: saleFlatNumber,
      isCurrentProperty,
    };
  });
  allSalesData.sort((a, b) => a.x - b.x);
  return allSalesData;
};

export const calculatePropertyTrends = (currentPropertyData) => {
  if (!currentPropertyData || currentPropertyData.length === 0) {
    return {
      firstSale: null,
      lastSale: null,
      priceDifference: 0,
      percentChange: 0,
      totalMonths: 0,
      annualizedRate: null,
      salesCount: 0,
    };
  }
  const sortedData = [...currentPropertyData].sort((a, b) => a.x - b.x);
  if (sortedData.length === 1) {
    const sale = sortedData[0];
    return {
      firstSale: sale,
      lastSale: sale,
      priceDifference: 0,
      percentChange: 0,
      totalMonths: 0,
      annualizedRate: null,
      salesCount: 1,
    };
  }
  const firstSale = sortedData[0];
  const lastSale = sortedData[sortedData.length - 1];
  const priceDifference = lastSale.y - firstSale.y;
  const percentChange = (priceDifference / firstSale.y) * 100;
  const firstDate = new Date(firstSale.x);
  const lastDate = new Date(lastSale.x);
  const yearDiff = lastDate.getFullYear() - firstDate.getFullYear();
  const monthDiff = lastDate.getMonth() - firstDate.getMonth();
  const totalMonths = yearDiff * 12 + monthDiff;
  let annualizedRate = null;
  if (totalMonths >= 12) {
    const years = totalMonths / 12;
    annualizedRate = (Math.pow(lastSale.y / firstSale.y, 1 / years) - 1) * 100;
  }
  return {
    firstSale,
    lastSale,
    priceDifference,
    percentChange,
    totalMonths,
    annualizedRate,
    salesCount: sortedData.length,
  };
};

export const prepareRegionalPriceData = (housePriceData, currentPropertyType) => {
  let regionPriceData = [];
  if (
    housePriceData?.regionPriceIndex?.monthlyData &&
    housePriceData.regionPriceIndex.monthlyData.length > 0
  ) {
    const propertyTypeToField = {
      terraced: 'averagePriceTerraced',
      'semi-detached': 'averagePriceSemiDetached',
      detached: 'averagePriceDetached',
      flat: 'averagePriceFlat',
      maisonette: 'averagePriceFlat',
    };
    const normalizedType = currentPropertyType.toLowerCase();
    let priceField = 'averagePrice'; // default overall field
    for (const [typeKey, fieldName] of Object.entries(propertyTypeToField)) {
      if (normalizedType.includes(typeKey)) {
        priceField = fieldName;
        break;
      }
    }
    regionPriceData = housePriceData.regionPriceIndex.monthlyData
      .filter((item) => item[priceField] !== undefined && item[priceField] !== null)
      .map((item) => ({
        x: new Date(item.date).getTime(),
        y: item[priceField],
        date: item.date,
        isRegionalAverage: true,
      }));
  }
  return regionPriceData;
};

export const calculateSameTypeStats = (sameTypeData) => {
  if (!sameTypeData || sameTypeData.length === 0) return null;
  const sortedData = [...sameTypeData].sort((a, b) => a.x - b.x);
  const firstSale = sortedData[0];
  const lastSale = sortedData[sortedData.length - 1];
  let annualIncrease = null;
  if (sortedData.length >= 2) {
    const firstDate = new Date(firstSale.x);
    const lastDate = new Date(lastSale.x);
    const yearDiff = lastDate.getFullYear() - firstDate.getFullYear();
    const monthDiff = lastDate.getMonth() - firstDate.getMonth();
    const dayDiff = lastDate.getDate() - firstDate.getDate();
    const totalYears = yearDiff + monthDiff / 12 + dayDiff / 365;
    const minYears = 0.25;
    if (totalYears >= minYears) {
      annualIncrease =
        (Math.pow(lastSale.y / firstSale.y, 1 / Math.max(totalYears, minYears)) - 1) * 100;
    }
  }
  return {
    count: sameTypeData.length,
    averagePrice: Math.round(
      sameTypeData.reduce((sum, item) => sum + item.y, 0) / sameTypeData.length,
    ),
    minPrice: Math.min(...sameTypeData.map((item) => item.y)),
    maxPrice: Math.max(...sameTypeData.map((item) => item.y)),
    annualIncrease,
  };
};
